import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DateRangePicker,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilRange,
  todayCivilDate,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilLine, Plus, RotateCcw, Truck, Archive } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  purchasingApi,
  type Supplier,
  type SupplierKind,
  type SupplierSort,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { PurchasingView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  activityOptions,
  formatDocument,
  formatPhone,
  supplierKindFilterOptions,
  supplierKindLabels,
  supplierKindOptions,
  type PurchasingCapabilities,
} from './shared.js';

const supplierSchema = z
  .object({
    kind: z.enum(['PERSON', 'COMPANY']),
    displayName: z
      .string()
      .trim()
      .min(2, 'Informe o nome do fornecedor.')
      .max(160, 'Use até 160 caracteres.'),
    legalName: z.string().trim().max(200, 'Use até 200 caracteres.'),
    document: z.string().trim().max(20, 'Use até 20 caracteres.'),
    email: z.string().trim().max(320, 'Use até 320 caracteres.'),
    phone: z.string().trim().max(20, 'Use até 20 caracteres.'),
    notes: z.string().trim().max(1000, 'Use até 1000 caracteres.'),
  })
  .strict();
type SupplierForm = z.infer<typeof supplierSchema>;

const columns: Array<{ sort: SupplierSort; label: string }> = [
  { sort: 'displayName', label: 'Fornecedor' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

export function SuppliersPanel({
  capabilities,
  timeZone,
  view,
  onView,
}: {
  capabilities: PurchasingCapabilities;
  timeZone: string;
  view: PurchasingView;
  onView: ViewSetter<PurchasingView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [activationTarget, setActivationTarget] = useState<Supplier>();
  const [activationError, setActivationError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    activity: view.activity,
    kind: view.supplierKind,
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'purchasing', 'suppliers', filters],
    queryFn: () => purchasingApi.listSuppliers(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'purchasing', 'suppliers', view.editSupplier],
    queryFn: () => purchasingApi.getSupplier(view.editSupplier!),
    enabled: Boolean(view.editSupplier),
    retry: false,
  });

  function toggleSort(column: SupplierSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  async function setActive(supplier: Supplier) {
    setActivationError(undefined);
    try {
      await purchasingApi.setSupplierActive(supplier.id, !supplier.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
      setActivationTarget(undefined);
      snackbar.success(supplier.active ? 'Fornecedor arquivado.' : 'Fornecedor reativado.');
    } catch (error) {
      setActivationError(getApiError(error));
    }
  }

  const total = page.data?.total ?? 0;
  const chips = [
    ...(view.activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ]),
    ...(view.supplierKind === 'ALL'
      ? []
      : [
          {
            key: 'supplierKind',
            label: supplierKindFilterOptions.find((item) => item.value === view.supplierKind)!
              .label,
            onRemove: () => onView({ supplierKind: 'ALL' }),
          },
        ]),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="mb-5 flex justify-end">
        {capabilities.canManageSuppliers ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Cadastrar fornecedor
          </Button>
        ) : null}
      </div>

      <FilterBar
        searchId="purchasing-supplier-search"
        searchLabel="Buscar fornecedor"
        searchPlaceholder="Nome, razão social, documento ou e-mail"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: 'ALL', supplierKind: 'ALL', from: null, to: null })}
        count={`${total} ${total === 1 ? 'fornecedor' : 'fornecedores'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'fornecedor' : 'fornecedores'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar fornecedores por situação"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <FilterGroup
          label="Tipo"
          description="Filtrar fornecedores por pessoa ou empresa"
          options={supplierKindFilterOptions}
          value={view.supplierKind}
          onChange={(supplierKind) => onView({ supplierKind })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Cadastro</span>
          <DateRangePicker
            label="Dias de cadastro"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando fornecedores" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Truck}
          title="Nenhum fornecedor encontrado"
          description="Ajuste os filtros ou cadastre quem vende para a empresa."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação dos fornecedores"
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.suppliers.map((supplier) => (
                  <li key={supplier.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                        <Truck className="size-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <strong className="block truncate text-sm">{supplier.displayName}</strong>
                        <span className="mt-1 block truncate text-xs text-content-muted">
                          {formatDocument(supplier.document) ?? 'Sem documento'}
                        </span>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-content-muted">
                          <span>{supplierKindLabels[supplier.kind]}</span>
                          <span>{supplier.email ?? 'Sem e-mail'}</span>
                        </div>
                        {supplier.active ? null : (
                          <div className="mt-2">
                            <StatusPill tone="neutral">Arquivado</StatusPill>
                          </div>
                        )}
                      </div>
                      <Actions
                        supplier={supplier}
                        capabilities={capabilities}
                        onEdit={() => onView({ editSupplier: supplier.id })}
                        onActivation={() => setActivationTarget(supplier)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Fornecedores da empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Documento
                  </th>
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Contato
                  </th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{supplier.displayName}</strong>
                      <span className="mt-1 block truncate text-xs text-content-muted">
                        {supplier.legalName ?? supplierKindLabels[supplier.kind]}
                      </span>
                      {supplier.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDate(supplier.createdAt, timeZone)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDocument(supplier.document) ?? 'Sem documento'}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      <span className="block truncate">{supplier.email ?? 'Sem e-mail'}</span>
                      <span className="block">{formatPhone(supplier.phone) ?? 'Sem telefone'}</span>
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        supplier={supplier}
                        capabilities={capabilities}
                        onEdit={() => onView({ editSupplier: supplier.id })}
                        onActivation={() => setActivationTarget(supplier)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'fornecedor' : 'fornecedores')}
          />
        </div>
      )}

      <SupplierDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        supplier={null}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
          setCreateOpen(false);
          snackbar.success('Fornecedor cadastrado.');
        }}
      />
      <SupplierDialog
        open={Boolean(view.editSupplier)}
        onOpenChange={(open) => {
          if (!open) onView({ editSupplier: undefined });
        }}
        supplier={detail.data ?? null}
        loading={detail.isLoading}
        {...(detail.error ? { error: getApiError(detail.error) } : {})}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'purchasing'] });
          onView({ editSupplier: undefined });
          snackbar.success('Fornecedor atualizado.');
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={activationTarget.active ? 'Arquivar fornecedor' : 'Reativar fornecedor'}
          description={
            activationTarget.active
              ? 'O fornecedor sai da operação, mas o cadastro e as ofertas permanecem.'
              : 'O fornecedor volta a ficar disponível para novas ofertas.'
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setActivationTarget(undefined)}>
                Cancelar
              </Button>
              <Button onClick={() => void setActive(activationTarget)}>
                {activationTarget.active ? 'Arquivar fornecedor' : 'Reativar fornecedor'}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          {activationError ? <RequestAlert message={activationError} /> : null}
        </Modal>
      ) : null}
    </>
  );
}

function Actions({
  supplier,
  capabilities,
  onEdit,
  onActivation,
}: {
  supplier: Supplier;
  capabilities: PurchasingCapabilities;
  onEdit: () => void;
  onActivation: () => void;
}) {
  if (!capabilities.canManageSuppliers) return null;
  return (
    <div className="flex justify-end gap-1">
      <IconButton
        label="Editar fornecedor"
        icon={<PencilLine className="size-4" />}
        onClick={onEdit}
      />
      <IconButton
        label={supplier.active ? 'Arquivar' : 'Reativar'}
        {...(supplier.active ? { tone: 'danger' as const } : {})}
        icon={supplier.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
        onClick={onActivation}
      />
    </div>
  );
}

function SupplierDialog({
  open,
  onOpenChange,
  supplier,
  loading = false,
  error,
  capabilities,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: Supplier | null;
  loading?: boolean;
  error?: string;
  capabilities: PurchasingCapabilities;
  onSaved: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<SupplierForm>({
    resolver: zodResolver(supplierSchema),
    defaultValues: emptySupplier(),
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  useEffect(() => {
    if (open) form.reset(supplier ? formOf(supplier) : emptySupplier());
  }, [form, open, supplier]);

  /// Fechar o diálogo apaga a falha do pedido anterior: reabrir para tentar de novo não pode
  /// começar mostrando o erro da tentativa passada.
  function changeOpen(next: boolean) {
    if (!next) setRequestError(undefined);
    onOpenChange(next);
  }

  async function save(data: SupplierForm) {
    setRequestError(undefined);
    const payload = {
      kind: data.kind,
      displayName: data.displayName,
      legalName: data.legalName || null,
      document: data.document || null,
      email: data.email || null,
      phone: data.phone || null,
      notes: data.notes || null,
    };
    try {
      if (supplier) await purchasingApi.updateSupplier(supplier.id, payload);
      else await purchasingApi.createSupplier(payload);
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={supplier ? 'Editar fornecedor' : 'Cadastrar fornecedor'}
      description="Quem vende para a empresa. O que cada um vende, e por quanto, são as ofertas."
      footer={
        !loading && !error && capabilities.canManageSuppliers ? (
          <>
            <FormErrorSummary
              form={'purchasing-supplier-form'}
              className="basis-full sm:basis-auto sm:flex-1"
            />
            <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="purchasing-supplier-form"
              loading={form.formState.isSubmitting}
            >
              {supplier ? 'Salvar fornecedor' : 'Cadastrar fornecedor'}
            </Button>
          </>
        ) : undefined
      }
    >
      {loading ? (
        <LoadingCard label="Carregando fornecedor" />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <form
          id="purchasing-supplier-form"
          noValidate
          onSubmit={(event) => void form.handleSubmit(save)(event)}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Input
                label="Nome do fornecedor"
                error={form.formState.errors.displayName?.message}
                {...form.register('displayName')}
              />
            </div>
            <Select
              label="Tipo"
              value={kind}
              options={supplierKindOptions}
              onValueChange={(value) => {
                form.setValue('kind', value as SupplierKind, { shouldValidate: true });
                /// Razão social é nome de registro de empresa: ao virar pessoa, o campo some e o
                /// que havia nele some junto. Guardá-lo escondido faria a API recusar por um valor
                /// que ninguém mais vê.
                if (value === 'PERSON') form.setValue('legalName', '');
              }}
            />
            <Input
              label={kind === 'PERSON' ? 'CPF (opcional)' : 'CNPJ (opcional)'}
              error={form.formState.errors.document?.message}
              {...form.register('document')}
            />
            {kind === 'COMPANY' ? (
              <div className="sm:col-span-2">
                <Input
                  label="Razão social (opcional)"
                  error={form.formState.errors.legalName?.message}
                  {...form.register('legalName')}
                />
              </div>
            ) : null}
            <Input
              label="E-mail (opcional)"
              error={form.formState.errors.email?.message}
              {...form.register('email')}
            />
            <Input
              label="Telefone (opcional)"
              error={form.formState.errors.phone?.message}
              {...form.register('phone')}
            />
            <div className="sm:col-span-2">
              <Textarea
                label="Observação"
                rows={3}
                error={form.formState.errors.notes?.message}
                {...form.register('notes')}
              />
            </div>
          </div>

          {requestError ? <RequestAlert message={requestError} /> : null}
        </form>
      )}
    </Modal>
  );
}

function emptySupplier(): SupplierForm {
  return {
    kind: 'COMPANY',
    displayName: '',
    legalName: '',
    document: '',
    email: '',
    phone: '',
    notes: '',
  };
}

function formOf(supplier: Supplier): SupplierForm {
  return {
    kind: supplier.kind,
    displayName: supplier.displayName,
    legalName: supplier.legalName ?? '',
    document: formatDocument(supplier.document) ?? '',
    email: supplier.email ?? '',
    phone: formatPhone(supplier.phone) ?? '',
    notes: supplier.notes ?? '',
  };
}
